import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { useMemo } from "react";

import { CrsRule, SiteWafBreakdownItem } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import Card from "~/components/ui/card/Card";
import DataTable, {
  DataTableColumnMeta,
} from "~/components/ui/dataTable/DataTable";

import { formatChartValue } from "./BreakdownChart";
import styles from "./BreakdownTable.module.scss";

type WafBreakdownTableProps = {
  data: SiteWafBreakdownItem[];
  isLoading: boolean;
};

const ACTION_CLASS: Record<SiteWafBreakdownItem["action"], string> = {
  log: styles.actionLog,
  allow: styles.actionAllow,
  deny: styles.actionDeny,
  detect: styles.actionDetect,
};

const ACTION_LABEL: Record<SiteWafBreakdownItem["action"], string> = {
  log: "Logged",
  allow: "Allowed",
  deny: "Denied",
  detect: "Detected",
};

const WafBreakdownTable = ({ data, isLoading }: WafBreakdownTableProps) => {
  const { data: crsData } = useQuery({
    queryKey: queryKeys.crsRules(),
    queryFn: () => apiClient.crsRules.crsRulesList(),
    staleTime: 10 * 60 * 1000,
  });

  const ruleMessageById = useMemo(() => {
    const map = new Map<number, string>();
    const rules: CrsRule[] = crsData?.data.crs_rules ?? [];
    for (const rule of rules) {
      if (rule.message) map.set(rule.id, rule.message);
    }

    return map;
  }, [crsData]);

  const columns = useMemo<ColumnDef<SiteWafBreakdownItem>[]>(
    () => [
      {
        accessorKey: "rule_id",
        header: "Rule ID",
        size: 115,
        cell: (info) => (info.getValue() as number) || "—",
      },
      {
        accessorKey: "directive_id",
        header: "Directive",
        size: 115,
        cell: (info) => {
          const id = info.getValue() as number;

          return id > 0 ? id : "—";
        },
      },
      {
        id: "description",
        header: "Description",
        meta: { autoWidth: true } satisfies DataTableColumnMeta,
        cell: ({ row }) => {
          const message = ruleMessageById.get(row.original.rule_id);

          return (
            <span
              className={styles.descriptionCell}
              title={message ?? undefined}
            >
              {message ?? "—"}
            </span>
          );
        },
      },
      {
        accessorKey: "action",
        header: "Action",
        size: 110,
        cell: ({ row }) => {
          const action = row.original.action;

          return (
            <span className={ACTION_CLASS[action]}>{ACTION_LABEL[action]}</span>
          );
        },
      },
      {
        accessorKey: "bot",
        header: "Origin",
        size: 110,
        cell: ({ row }) =>
          row.original.bot ? (
            <span className={styles.botBadge}>Bot</span>
          ) : (
            <span className={styles.humanBadge}>Human</span>
          ),
      },
      {
        accessorKey: "total",
        header: "Hits",
        size: 90,
        meta: { align: "right" } satisfies DataTableColumnMeta,
        cell: (info) => (
          <span className={styles.totalValue}>
            {formatChartValue(info.getValue() as number)}
          </span>
        ),
      },
    ],
    [ruleMessageById],
  );

  return (
    <div className={clsx(styles.root)}>
      <Card title="Triggered Rules">
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          skeletonRows={6}
          emptyText="No WAF events for this period."
          pageSize={20}
          initialSorting={[{ id: "total", desc: true }]}
        />
      </Card>
    </div>
  );
};

export default WafBreakdownTable;
